import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  Alert,
  Badge,
  Flex,
  IllustratedMessage,
  Spinner,
  Tooltip,
  Typography,
} from '@percent/lemonade'
import { groupDomainsByArea } from '../../utils/groupDomainsByArea'
import { foldImpliedRoles } from '../../utils/foldImpliedRoles'
import { heldRolesCount, includesLabel } from '../../utils/describeRoleSource'
import { RoleSources } from '../roleSources'
import type { EffectiveRole } from '../../types/effectiveRoles.types'
import type { EffectiveRolesListProps } from './EffectiveRolesList.types'
import styles from './EffectiveRolesList.module.scss'

// Up to this many lines show open; more - a Super Admin has one in every
// domain - start with each area collapsed to its count.
const OPEN_UP_TO = 8

// An included role also held for a reason of its own (assigned directly, say)
// gives that reason on hover or focus, so the line stays short.
type IncludedRoleBadgeProps = Readonly<{ role: EffectiveRole }>

function IncludedRoleBadge({ role }: IncludedRoleBadgeProps) {
  const badge = (
    <Badge variant="default" size="small">
      {role.name}
    </Badge>
  )

  if (role.sources.length === 0) {
    return badge
  }

  return (
    <Tooltip
      content={
        <div className={styles.tooltip}>
          <RoleSources sources={role.sources} className={styles.reasons} />
        </div>
      }
    >
      <span className={styles.alsoHeld} tabIndex={0}>
        {badge}
      </span>
    </Tooltip>
  )
}

export function EffectiveRolesList({
  effectiveDomains,
  emptyTitle,
  isLoading = false,
  errorMessage,
  getGroupHref,
}: EffectiveRolesListProps) {
  const { t } = useTranslation()

  const areas = useMemo(
    () =>
      groupDomainsByArea(
        [...effectiveDomains]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((domain) => ({ ...domain, held: foldImpliedRoles(domain) }))
      ),
    [effectiveDomains]
  )
  const lineCount = areas
    .flatMap(({ domains }) => domains)
    .reduce((count, domain) => count + domain.held.length, 0)

  // Unset until the viewer opens or closes an area, so the default follows
  // the data, which arrives after the first render. No expand-all here: the
  // permissions list beside it has one, and two on a page are ambiguous.
  const [chosen, setChosen] = useState<string[]>()
  const open =
    chosen ?? (lineCount > OPEN_UP_TO ? [] : areas.map(({ area }) => area))

  if (isLoading) {
    return (
      <Flex justify="center" py="xl">
        <Spinner size="l" />
      </Flex>
    )
  }

  if (errorMessage) {
    return <Alert variant="error">{errorMessage}</Alert>
  }

  if (effectiveDomains.length === 0) {
    return <IllustratedMessage illustration="no-results" title={emptyTitle} />
  }

  return (
    <Flex direction="column" gap="xs" data-testid="effective-roles">
      <Accordion
        variant="default"
        value={open}
        onChange={(value: string | string[] | null) =>
          setChosen(Array.isArray(value) ? value : value ? [value] : [])
        }
        multiple
      >
        {areas.map(({ area, domains }) => (
          <Accordion.Item
            key={area}
            value={area}
            data-testid={`effective-area-${area}`}
          >
            <Accordion.Control data-testid={`effective-area-${area}-control`}>
              <Flex
                justify="space-between"
                align="center"
                gap={4}
                className={styles.areaHeader}
              >
                <Typography variant="default-medium">{area}</Typography>
                <Badge variant="informative" shape="oval" size="small">
                  {heldRolesCount(
                    domains.reduce((count, d) => count + d.roles.length, 0),
                    t
                  )}
                </Badge>
              </Flex>
            </Accordion.Control>
            <Accordion.Panel>
              <div className={styles.panel}>
                <div className={styles.domains}>
                  {domains.map((domain) => (
                    <div
                      key={domain.id}
                      className={styles.domain}
                      data-testid={`effective-domain-${domain.id}`}
                    >
                      {domain.held.map((role, index) => (
                        <div
                          key={role.id}
                          className={styles.row}
                          data-testid={`effective-role-${domain.id}-${role.id}`}
                        >
                          <Typography
                            variant="small"
                            className={styles.domainName}
                          >
                            {index === 0 && domain.name}
                          </Typography>
                          <div className={styles.held}>
                            <Badge variant="default">{role.name}</Badge>
                            {role.includes.length > 0 && (
                              <div className={styles.includes}>
                                <span className={styles.includesLabel}>
                                  {includesLabel(t)}
                                </span>
                                <div className={styles.includedRoles}>
                                  {role.includes.map((included) => (
                                    <IncludedRoleBadge
                                      key={included.id}
                                      role={included}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <RoleSources
                            sources={role.sources}
                            getGroupHref={getGroupHref}
                            className={styles.reasons}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Flex>
  )
}
